// Venue-search VS1.3. The required and showIf rules of the field dictionary,
// as code: one place that says "this field shows" and "this field is missing".
// `values` is a flat map of field ID -> value, in the dictionary's shapes
// (IDs, numbers, true/false, "HH:MM", {cents, currency}). A missing key is
// "not answered" (3-state yes/no: blank is never no).
import { venueField } from '.';

const numberOf = (value) => (value && typeof value === 'object' && Number.isInteger(value.cents) ? value.cents : value);

export const conditionHolds = (condition, values) => {
  const value = values[condition.field];
  if ('equals' in condition) return value === condition.equals;
  if ('in' in condition) return condition.in.includes(value);
  if ('greaterThan' in condition) {
    const number = numberOf(value);
    return typeof number === 'number' && number > condition.greaterThan;
  }
  return false;
};

export const isShown = (id, values) => {
  const { showIf } = venueField(id);
  return !showIf || conditionHolds(showIf, values);
};

export const isRequired = (id, values) => {
  const { required } = venueField(id);
  if (typeof required === 'object') return conditionHolds(required.if, values);
  return required === true;
};

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

// Is there an answer of the right shape? (Not: is the answer allowed.)
export const isAnswered = (id, value) => {
  if (value === undefined || value === null) return false;
  const { type } = venueField(id);
  switch (type) {
    case 'yesNo':
      return typeof value === 'boolean';
    case 'integer':
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'time':
      return TIME.test(value);
    case 'choice':
      return typeof value === 'string' ? value !== '' : !!(value && value.list);
    case 'choices':
      return Array.isArray(value) || (value && Array.isArray(value.list));
    case 'counts':
      return typeof value === 'object' && Object.values(value).some((count) => count > 0);
    case 'money':
      return Number.isInteger(value.cents) && !!value.currency;
    case 'text':
      return typeof value === 'string' && value.trim() !== '';
    default:
      return true;
  }
};

const minutes = (time) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

// Breaks of a field's `rule` (min, max, maxLength, minHoursAfter), for an answered value.
export const ruleProblem = (id, value, values) => {
  const { rule } = venueField(id);
  if (!rule) return null;
  const number = numberOf(value);
  if (typeof number === 'number') {
    if (rule.min !== undefined && number < rule.min) return 'min';
    if (rule.max !== undefined && number > rule.max) return 'max';
  }
  if (rule.minHoursAfter && TIME.test(value)) {
    const start = values[rule.minHoursAfter.field];
    if (TIME.test(start || '')) {
      if (minutes(value) <= minutes(start)) return 'endBeforeStart';
      if (minutes(value) - minutes(start) < rule.minHoursAfter.hours * 60) return 'tooShort';
    }
  }
  return null;
};

// { id: 'missing' | rule problem } for the given fields. Hidden fields are skipped.
export const fieldProblems = (ids, values) =>
  ids.reduce((problems, id) => {
    if (!isShown(id, values)) return problems;
    const value = values[id];
    if (!isAnswered(id, value)) {
      if (isRequired(id, values)) problems[id] = 'missing';
      return problems;
    }
    const problem = ruleProblem(id, value, values);
    if (problem) problems[id] = problem;
    return problems;
  }, {});
